import { useMutation, useQuery } from '@tanstack/react-query';
import {
  useActiveAccountId,
  useActiveProjectCohortId,
  useActiveProjectId,
  useNotify,
  useServiceAuth,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';

// Typed instance used for list queries (FhirService.get); generic instance used
// for single-resource fetch (openGet) and version-aware writes (openPut).
const documentReferenceFhirService = new FhirService('DocumentReference');
const fhirService = new FhirService();

const DATASET_SYSTEM = 'http://lifeomic.com/fhir/dataset';
const DOCUMENT_TYPE_SYSTEM = 'http://lifeomic.com/fhir/document-type';
const GENOMIC_FILE_CODE = 'genomic-file';
const RESOURCE_VISIBILITY_SYSTEM =
  'http://lifeomic.com/fhir/resource-visibility';
const CLINIC_ONLY_CODE = 'clinic-only';
const PAGE_SIZE = 1000;
const MAX_CONFLICT_RETRIES = 3;

const hasClinicOnlyTag = (documentReference: fhir.DocumentReference): boolean =>
  (documentReference.meta?.tag ?? []).some(
    tag =>
      tag.system === RESOURCE_VISIBILITY_SYSTEM &&
      tag.code === CLINIC_ONLY_CODE,
  );

const areMemberFilesVisible = (documentReferences: fhir.DocumentReference[]) =>
  documentReferences.some(doc => !hasClinicOnlyTag(doc));

const setMemberVisibility = (
  documentReference: fhir.DocumentReference,
  shouldBeVisibleToMember: boolean,
): fhir.DocumentReference => {
  const updatedDocumentReference: fhir.DocumentReference = JSON.parse(
    JSON.stringify(documentReference),
  );
  const currentTags = updatedDocumentReference.meta?.tag ?? [];
  const tagsWithoutClinicOnly = currentTags.filter(
    tag =>
      !(
        tag.system === RESOURCE_VISIBILITY_SYSTEM &&
        tag.code === CLINIC_ONLY_CODE
      ),
  );

  updatedDocumentReference.meta = updatedDocumentReference.meta || {};
  updatedDocumentReference.meta.tag = shouldBeVisibleToMember
    ? tagsWithoutClinicOnly
    : [
        ...tagsWithoutClinicOnly,
        {
          system: RESOURCE_VISIBILITY_SYSTEM,
          code: CLINIC_ONLY_CODE,
        },
      ];

  return updatedDocumentReference;
};

const sleep = (durationMs: number) =>
  new Promise(resolve => globalThis.setTimeout(resolve, durationMs));

const getLatestDocumentReference = async ({
  accountId,
  cohortId,
  documentReferenceId,
  serviceAuth,
}: {
  accountId: string;
  cohortId?: string;
  documentReferenceId: string;
  serviceAuth: ReturnType<typeof useServiceAuth>;
}) => {
  const response = await fhirService.openGet(serviceAuth)(
    accountId,
    `DocumentReference/${documentReferenceId}`,
    { cohort: cohortId },
  );

  return response.data as fhir.DocumentReference;
};

type UseGenomicDocumentReferencesVisibilityArgs = {
  enabled?: boolean;
  subjectId?: string;
};

export const useGenomicDocumentReferencesVisibility = ({
  enabled = true,
  subjectId,
}: UseGenomicDocumentReferencesVisibilityArgs) => {
  const serviceAuth = useServiceAuth();
  const accountId = useActiveAccountId();
  const projectId = useActiveProjectId();
  const cohortId = useActiveProjectCohortId();
  const [notify] = useNotify();

  const genomicDocumentReferencesQuery = useQuery(
    [
      'member-genomic-document-references',
      accountId,
      projectId,
      cohortId,
      subjectId,
    ],
    async () => {
      if (!serviceAuth || !accountId || !projectId || !subjectId) {
        return [];
      }

      const response = await documentReferenceFhirService.get(serviceAuth)(
        accountId,
        {
          pageSize: PAGE_SIZE,
          patient: subjectId,
          cohort: cohortId,
          _tag: [
            `${DATASET_SYSTEM}|${projectId}`,
            `${DOCUMENT_TYPE_SYSTEM}|${GENOMIC_FILE_CODE}`,
          ],
        },
      );

      return (response.data.entry ?? [])
        .map(entry => entry.resource as fhir.DocumentReference | undefined)
        .filter((doc): doc is fhir.DocumentReference => Boolean(doc));
    },
    {
      enabled:
        enabled && !!serviceAuth && !!accountId && !!projectId && !!subjectId,
      staleTime: 0,
      retry: false,
    },
  );

  const genomicDocumentReferences = genomicDocumentReferencesQuery.data ?? [];
  const hasGenomicDocumentReferences = genomicDocumentReferences.length > 0;
  const currentAreMemberFilesVisible = areMemberFilesVisible(
    genomicDocumentReferences,
  );

  const updateDocumentReferenceVisibility = async (
    documentReference: fhir.DocumentReference,
    shouldBeVisibleToMember: boolean,
  ) => {
    if (!documentReference.id) {
      throw new Error('DocumentReference is missing an id.');
    }

    let currentDocumentReference = documentReference;

    for (let attempt = 0; attempt < MAX_CONFLICT_RETRIES; attempt += 1) {
      const currentDocumentReferenceId = currentDocumentReference.id;

      if (!currentDocumentReferenceId) {
        throw new Error('DocumentReference is missing an id.');
      }

      const updatedDocumentReference = setMemberVisibility(
        currentDocumentReference,
        shouldBeVisibleToMember,
      );

      try {
        await fhirService.openPut(serviceAuth)({
          account: accountId,
          resourceType: 'DocumentReference',
          id: currentDocumentReferenceId,
          updatedResource: updatedDocumentReference,
          cohortId: cohortId || undefined,
          confirmNoConflict: true,
        });
        return;
      } catch (error: any) {
        const canRetry =
          error?.response?.status === 409 && attempt < MAX_CONFLICT_RETRIES - 1;

        if (!canRetry) {
          throw error;
        }

        await sleep(300);
        currentDocumentReference = await getLatestDocumentReference({
          accountId,
          cohortId: cohortId || undefined,
          documentReferenceId: currentDocumentReferenceId,
          serviceAuth,
        });
      }
    }
  };

  const getLatestGenomicDocumentReferences = async () => {
    const queryResult = await genomicDocumentReferencesQuery.refetch();

    return queryResult.data ?? [];
  };

  const toggleVisibilityMutation = useMutation({
    mutationFn: async () => {
      if (!serviceAuth || !accountId) {
        return currentAreMemberFilesVisible;
      }

      // First refetch gets fresh server state so we toggle based on the latest
      // visibility, not potentially stale cached data.
      const latestDocumentReferences =
        await getLatestGenomicDocumentReferences();

      if (latestDocumentReferences.length === 0) {
        return areMemberFilesVisible(latestDocumentReferences);
      }

      const shouldBeVisibleToMember = !areMemberFilesVisible(
        latestDocumentReferences,
      );

      for (const documentReference of latestDocumentReferences) {
        await updateDocumentReferenceVisibility(
          documentReference,
          shouldBeVisibleToMember,
        );
      }

      return shouldBeVisibleToMember;
    },
    onSuccess: async shouldBeVisibleToMember => {
      notify(
        shouldBeVisibleToMember
          ? 'Genomic files are now visible in the member app.'
          : 'Genomic files are now hidden from the member app.',
        'success',
      );
      // Second refetch updates the UI to reflect the writes just made.
      await genomicDocumentReferencesQuery.refetch();
    },
    onError: error => {
      console.error(error);
      notify('Error updating genomic file visibility.', 'error');
    },
  });

  return {
    areMemberFilesVisible: currentAreMemberFilesVisible,
    hasGenomicDocumentReferences,
    isLoading: genomicDocumentReferencesQuery.isLoading,
    isUpdating:
      toggleVisibilityMutation.isLoading ||
      genomicDocumentReferencesQuery.fetchStatus === 'fetching',
    toggleVisibility: toggleVisibilityMutation.mutate,
  };
};
